/**
 * §B9's `#evenNote` — *"the single most useful supervision artefact on the page"*.
 *
 * Across the same matches, how many umpires each set of appointments actually uses and the
 * heaviest load any one of them carries, for the Umpires Managers and for the engine, side by
 * side. Three rows, never four: `On-Field`, `Reserve`, `Video`, and no combined row for the
 * reason §B6 gives for having no combined column.
 *
 * **Both halves are measured by `summariseUmpireEvenness` from `@hockeytd/shared`** — the same
 * function the API runs — so the two figures being compared cannot be arrived at two different
 * ways. The engine's own comment on it says exactly that: *"this is the arithmetic, once, so
 * the two halves of that comparison cannot be measured differently."*
 *
 * **Both halves are measured over the SAME fixtures**, the ones the AI matrix panel's own
 * competition tab is showing. A comparison whose two sides counted different matches would be
 * worse than no comparison at all.
 *
 * **RUN 81 — the Umpires Managers' side can be absent.** The AI half now opens with the draft
 * this system already holds (§Panel 5a), while the TMS half still waits for `Load Appointments`,
 * so the note can be on screen with only one of its two measurements to print. It prints that
 * one — the engine's figures, as ever — and `Not Loaded This Session` where the Umpires
 * Managers' would be. It never stands zeros in for them: *"0 umpires used"* is a measurement,
 * and beside a full draft it would raise `Engine Spreads Wider` over appointments nobody has
 * read yet.
 */

import { summariseUmpireEvenness, type UmpireEvenness, type UmpireWorkload } from "@hockeytd/shared";

import type { UmpireMatrixTable } from "./derive.js";

/** The matrix rows, as the shared summariser takes them. No total is formed on the way. */
export function workloadsOf(table: UmpireMatrixTable): UmpireWorkload[] {
  return table.rows.map((row) => ({
    official_id: row.umpire.tmsPersonId,
    on_field: row.onField,
    video: row.video,
    reserve: row.reserve,
  }));
}

export interface EvennessNoteProps {
  /**
   * The Umpires Managers' matrix, under the AI panel's own filter — or `null` while
   * `Load Appointments` has not been pressed this session (RUN 81).
   */
  actual: UmpireMatrixTable | null;
  /** The engine's matrix, under the same filter. */
  reference: UmpireMatrixTable;
}

/** §B9's three labels, in §B9's order, each naming the field it reads. */
const ROWS: readonly { label: string; key: keyof UmpireEvenness }[] = [
  { label: "On-Field", key: "on_field" },
  { label: "Reserve", key: "reserve" },
  { label: "Video", key: "video" },
];

/**
 * §B9's flag: *"when the AI is better (more umpires used, or the same number used with a lower
 * maximum)"*. Nothing is claimed when the engine is level or worse — a chip that appeared
 * either way would be a decoration rather than a finding.
 */
export function engineSpreadsWider(
  actual: { used: number; max: number },
  reference: { used: number; max: number },
): boolean {
  if (reference.used > actual.used) return true;
  return reference.used === actual.used && reference.max < actual.max;
}

export function EvennessNote({ actual, reference }: EvennessNoteProps): React.JSX.Element {
  const actualEvenness = actual === null ? null : summariseUmpireEvenness(workloadsOf(actual));
  const referenceEvenness = summariseUmpireEvenness(workloadsOf(reference));
  // Both matrices are built over the same matches, so either count is the count — and the
  // engine's is the one that is always there.
  const matches = actual?.fixtures ?? reference.fixtures;

  return (
    <div className="rulenote" data-testid="evenness-note">
      <b>Evenness at a glance —</b> across the same {matches}{" "}
      {matches === 1 ? "match" : "matches"}, how many umpires each set of appointments
      actually uses, and the heaviest load carried by any one of them:
      <span className="evenlist">
        {ROWS.map(({ label, key }) => {
          const left = actualEvenness === null ? null : actualEvenness[key];
          const right = referenceEvenness[key];
          return (
            <span className="evenrow" key={key} data-testid={`evenness-${key}`}>
              <span className="evenlab">{label}</span>
              {left === null ? (
                <span>Umpires Managers — Not Loaded This Session</span>
              ) : (
                <span>
                  Umpires Managers <b>{left.used}</b> umpires used, heaviest load <b>{left.max}</b>
                </span>
              )}
              <span className="evensep" aria-hidden="true">
                ·
              </span>
              <span>
                AI draft <b>{right.used}</b> used, heaviest <b>{right.max}</b>
              </span>
              {/* A finding needs both sides: with the TMS half not loaded there is nothing to
                  say the engine spreads wider THAN. */}
              {left !== null && engineSpreadsWider(left, right) ? (
                <span className="evenflag" data-testid={`evenness-wider-${key}`}>
                  Engine Spreads Wider
                </span>
              ) : null}
            </span>
          );
        })}
      </span>
    </div>
  );
}
